import type { HullId } from "@frontier/content/definitions";
import { MAX_SHIP_CELLS } from "@frontier/content/ship-fitting";
import type { Point } from "./geometry";

export interface WeaponMountBank {
  single: readonly [number, number];
  front: readonly [number, number];
  rear: readonly [number, number];
}

/** Texture coordinates: a centre gun and two mirrored rows along the hull. */
export const HULL_WEAPON_MOUNTS: Record<HullId, WeaponMountBank> = {
  assault: { single: [0.5, 0.26], front: [0.31, 0.58], rear: [0.31, 0.78] },
  interceptor: { single: [0.5, 0.2], front: [0.18, 0.53], rear: [0.27, 0.6] },
  engineer: { single: [0.5, 0.14], front: [0.3, 0.56], rear: [0.25, 0.75] },
  scout: { single: [0.5, 0.22], front: [0.32, 0.5], rear: [0.24, 0.72] },
  transport: { single: [0.5, 0.2], front: [0.37, 0.46], rear: [0.25, 0.76] },
  bastion: { single: [0.5, 0.2], front: [0.27, 0.35], rear: [0.18, 0.72] },
  hawk: { single: [0.5, 0.3], front: [0.37, 0.66], rear: [0.2, 0.73] },
  aerie: { single: [0.5, 0.2], front: [0.27, 0.55], rear: [0.25, 0.76] },
  arrow: { single: [0.5, 0.2], front: [0.45, 0.4], rear: [0.42, 0.69] },
  argosy: { single: [0.5, 0.2], front: [0.38, 0.45], rear: [0.27, 0.72] },
  leviathan: { single: [0.5, 0.18], front: [0.32, 0.52], rear: [0.25, 0.77] },
  quicksilver: { single: [0.5, 0.54], front: [0.25, 0.25], rear: [0.24, 0.52] },
  falcon: { single: [0.5, 0.22], front: [0.3, 0.52], rear: [0.16, 0.73] },
  clipper: { single: [0.5, 0.2], front: [0.3, 0.47], rear: [0.16, 0.74] },
  behemoth: { single: [0.5, 0.19], front: [0.32, 0.41], rear: [0.23, 0.7] },
};

export function weaponMountUv(
  bank: WeaponMountBank,
  ordinal: number,
  count: number,
  out: Point,
): boolean {
  if (
    !Number.isInteger(count) ||
    !Number.isInteger(ordinal) ||
    count < 1 ||
    count > MAX_SHIP_CELLS ||
    ordinal < 0 ||
    ordinal >= count
  )
    return false;
  const centre = count % 2;
  if (centre && ordinal === 0) {
    [out.x, out.y] = bank.single;
    return true;
  }
  const pair = Math.floor((ordinal - centre) / 2);
  const pairs = Math.floor(count / 2);
  const t = pairs > 1 ? pair / (pairs - 1) : 0;
  const x = bank.front[0] + (bank.rear[0] - bank.front[0]) * t;
  out.x = (ordinal - centre) % 2 ? 1 - x : x;
  out.y = bank.front[1] + (bank.rear[1] - bank.front[1]) * t;
  return true;
}

export function weaponMountPosition(
  bank: WeaponMountBank,
  ordinal: number,
  count: number,
  width: number,
  length: number,
  origin: Point,
  heading: number,
  out: Point,
): boolean {
  if (!weaponMountUv(bank, ordinal, count, out)) return false;
  const forward = (0.5 - out.y) * length;
  const side = (out.x - 0.5) * width;
  const cosine = Math.cos(heading),
    sine = Math.sin(heading);
  out.x = origin.x + forward * cosine - side * sine;
  out.y = origin.y + forward * sine + side * cosine;
  return true;
}
